<template>
  <header class="navbar" :class="{ 'scrolled': isScrolled }">
    <div class="navbar-container">
      <div class="logo">
        <router-link to="/">
          <img src="/icons/icon.svg" alt="WhisperHerb Logo" class="logo-image">
          <span class="logo-text">{{ $t('app.name') }}</span>
          <span class="beta-label">beta</span>
        </router-link>
      </div>
      
      <!-- Desktop Navigation -->
      <nav class="desktop-nav">
        <router-link to="/" class="nav-link" exact-active-class="active">{{ $t('nav.home') }}</router-link>
        <router-link to="/analyze" class="nav-link" active-class="active">{{ $t('nav.analyze') }}</router-link>
        <router-link v-if="isAuthenticated" to="/my-plants" class="nav-link" active-class="active">{{ $t('nav.my_plants') }}</router-link>
        <router-link to="/about" class="nav-link" active-class="active">{{ $t('nav.about') }}</router-link>
        <router-link to="/contact" class="nav-link" active-class="active">{{ $t('nav.contact') }}</router-link>
        <InstallButton />
        <template v-if="isAuthenticated">
          <div class="user-menu">
            <button @click="toggleUserMenu" class="user-menu-button">
              <span class="user-avatar">{{ userInitials }}</span>
              <span class="username">{{ userName }}</span>
              <span class="arrow-icon" :class="{ 'open': userMenuOpen }">▼</span>
            </button>
            <div v-show="userMenuOpen" class="user-dropdown">
              <router-link to="/profile" class="dropdown-item">{{ $t('nav.profile') }}</router-link>
              <router-link to="/settings" class="dropdown-item">{{ $t('nav.settings') }}</router-link>
              <div class="dropdown-divider"></div>
              <button @click="logout" class="dropdown-item logout-btn">{{ $t('nav.logout') }}</button>
            </div>
          </div>
        </template>
        <template v-else>
          <router-link to="/login" class="nav-link login-link">{{ $t('nav.login') }}</router-link>
          <router-link to="/register" class="nav-link signup-link">{{ $t('nav.sign_up') }}</router-link>
        </template>
      </nav>
      
      <!-- Mobile Menu Button -->
      <button class="mobile-menu-btn" 
              @click="toggleMobileMenu" 
              :class="{ 'open': mobileMenuOpen }"
              aria-label="Toggle menu">
        <span class="menu-bar"></span>
        <span class="menu-bar"></span>
        <span class="menu-bar"></span>
      </button>
    </div>
    
    <!-- Mobile Navigation -->
    <transition name="slide-down">
      <nav v-show="mobileMenuOpen" class="mobile-nav">
        <router-link to="/" class="nav-link" exact-active-class="active" @click="closeMobileMenu">
          <span class="nav-icon">🏠</span>
          {{ $t('nav.home') }}
        </router-link>
        <router-link to="/analyze" class="nav-link" active-class="active" @click="closeMobileMenu">
          <span class="nav-icon">🔍</span>
          {{ $t('nav.analyze') }}
        </router-link>
        <router-link v-if="isAuthenticated" to="/my-plants" class="nav-link" active-class="active" @click="closeMobileMenu">
          <span class="nav-icon">🌿</span>
          {{ $t('nav.my_plants') }}
        </router-link>
        <button v-if="shouldShowInstall" @click="handleInstall" class="nav-link install-nav-link">
          <span class="nav-icon">📱</span>
          {{ $t('install.button_text') }}
        </button>
        <router-link to="/about" class="nav-link" active-class="active" @click="closeMobileMenu">
          <span class="nav-icon">ℹ️</span>
          {{ $t('nav.about') }}
        </router-link>
        <router-link to="/contact" class="nav-link" active-class="active" @click="closeMobileMenu">
          <span class="nav-icon">📞</span>
          {{ $t('nav.contact') }}
        </router-link>
        <div class="mobile-nav-actions">
          <template v-if="isAuthenticated">
            <div class="user-info">
              <span class="user-avatar large">{{ userInitials }}</span>
              <div class="user-details">
                <span class="username">{{ userName }}</span>
                <span class="user-email">{{ userEmail }}</span>
              </div>
            </div>
            <div class="mobile-actions">
              <router-link to="/profile" class="nav-link" @click="closeMobileMenu">
                <span class="nav-icon">👤</span>
                {{ $t('nav.profile') }}
              </router-link>
              <router-link to="/settings" class="nav-link" @click="closeMobileMenu">
                <span class="nav-icon">⚙️</span>
                {{ $t('nav.settings') }}
              </router-link>
              <button @click="handleLogout" class="nav-link logout-btn">
                <span class="nav-icon">🚪</span>
                {{ $t('nav.logout') }}
              </button>
            </div>
          </template>
          <template v-else>
            <div class="auth-buttons">
              <router-link to="/login" class="btn btn-outline btn-block" @click="closeMobileMenu">
                {{ $t('nav.login') }}
              </router-link>
              <router-link to="/register" class="btn btn-block" @click="closeMobileMenu">
                {{ $t('nav.sign_up') }}
              </router-link>
            </div>
          </template>
        </div>
      </nav>
    </transition>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/authStore';
import InstallButton from './InstallButton.vue';

const router = useRouter();
const authStore = useAuthStore();
const mobileMenuOpen = ref(false);
const userMenuOpen = ref(false);
const isScrolled = ref(false);

const isAuthenticated = computed(() => authStore.isAuthenticated);
const shouldShowInstall = ref(false)

const userName = computed(() => {
  const user = authStore.user;
  if (!user) return 'User';
  return user.name || user.username || 'User';
});

const userEmail = computed(() => {
  const user = authStore.user;
  if (!user) return '';
  return user.email || '';
});

const userInitials = computed(() => {
  const user = authStore.user;
  if (!user) return 'U';
  
  const displayName = user.name || user.username || '';
  if (!displayName) return 'U';
  
  return displayName.charAt(0).toUpperCase();
});

// Watch for user changes
watch(() => authStore.user, (newUser) => {
  // User state changed
}, { immediate: true });

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
  if (mobileMenuOpen.value) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

const toggleUserMenu = () => {
  userMenuOpen.value = !userMenuOpen.value;
};

const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
  document.body.style.overflow = '';
};

const closeUserMenu = () => {
  userMenuOpen.value = false;
};

const handleLogout = () => {
  logout();
  closeMobileMenu();
};

const logout = async () => {
  closeUserMenu();
  authStore.logout();
  // 强制页面刷新，确保所有状态清除
  // 注释掉以下行来测试Pinia的持久化功能
  // window.location.href = '/';
  router.push('/');
};

const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

// Close user menu when clicking outside
const handleClickOutside = (event) => {
  const userMenu = document.querySelector('.user-menu');
  if (userMenuOpen.value && userMenu && !userMenu.contains(event.target)) {
    closeUserMenu();
  }
};

const checkInstallability = () => {
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                      (window.navigator.standalone === true)
  const isPwaInstalled = localStorage.getItem('pwaInstalled') === 'true'
  const hasPWASupport = isPWACapableBrowser()
  
  shouldShowInstall.value = !isStandalone && !isPwaInstalled && hasPWASupport
}

const isPWACapableBrowser = () => {
  const ua = navigator.userAgent
  const supportedBrowsers = {
    isChromiumBased: /Chrome|Edg|OPR|Opera/i.test(ua),
    isFirefox: /Firefox/i.test(ua),
    isIOSSafari: /iPhone|iPad|iPod/i.test(ua) && /Safari/i.test(ua)
  }
  
  return supportedBrowsers.isChromiumBased || 
         supportedBrowsers.isFirefox || 
         supportedBrowsers.isIOSSafari
}

const handleInstall = async () => {
  if (window.PWAInstall?.deferredPrompt) {
    try {
      await window.PWAInstall.deferredPrompt.prompt()
      const { outcome } = await window.PWAInstall.deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwaInstalled', 'true')
        shouldShowInstall.value = false
      }
      
      window.PWAInstall.deferredPrompt = null
    } catch (error) {
      console.error('Native installation failed:', error)
      showFallbackInstructions()
    }
  } else {
    showFallbackInstructions()
  }
  closeMobileMenu()
}

const showFallbackInstructions = () => {
  if (window.PWAInstall?.showFallbackPrompt) {
    window.PWAInstall.showFallbackPrompt()
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  document.addEventListener('click', handleClickOutside);
  checkInstallability();
  
  window.addEventListener('appinstalled', () => {
    shouldShowInstall.value = false
  })
  
  const intervalId = setInterval(() => {
    checkInstallability()
  }, 30000)
  
  onUnmounted(() => {
    clearInterval(intervalId)
  })
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  document.removeEventListener('click', handleClickOutside);
});

// Removed menuInstall function - now handled by InstallButton component
</script>

<style scoped>
.navbar {
  background-color: var(--primary-color);
  color: white;
  box-shadow: var(--shadow-sm);
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  transition: 
    background-color var(--transition-normal),
    box-shadow var(--transition-normal),
    height var(--transition-normal);
}

.navbar.scrolled {
  background-color: rgba(45, 134, 68, 0.95);
  backdrop-filter: blur(8px);
  box-shadow: var(--shadow-md);
}

.navbar-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-md) var(--space-xl);
  max-width: var(--container-xl);
  margin: 0 auto;
  height: 70px;
}

.logo a {
  color: white;
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  transition: transform var(--transition-fast);
}

.logo a:hover {
  transform: scale(1.05);
}

.logo-image {
  height: 32px;
  width: auto;
  margin-right: var(--space-sm);
}

.logo-icon {
  margin-right: var(--space-sm);
  font-size: 1.75rem;
}

.desktop-nav {
  display: flex;
  gap: var(--space-xl);
  align-items: center;
}

.nav-link {
  color: white;
  text-decoration: none;
  padding: var(--space-xs) var(--space-xs);
  position: relative;
  font-size: 1rem;
  font-weight: 500;
  transition: color var(--transition-normal);
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.nav-link:hover {
  color: rgba(255, 255, 255, 0.9);
}

.nav-link.active {
  font-weight: 600;
}

.nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -3px;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: white;
  border-radius: 2px;
  transform: scaleX(0.7);
  opacity: 0.9;
  transition: transform var(--transition-normal), opacity var(--transition-normal);
}

.nav-link.active:hover::after {
  transform: scaleX(1);
  opacity: 1;
}

.login-link {
  margin-right: var(--space-sm);
}

.signup-link {
  background-color: white;
  color: var(--primary-color);
  padding: var(--space-sm) var(--space-lg);
  border-radius: var(--radius-md);
  transition: 
    background-color var(--transition-normal), 
    color var(--transition-normal),
    transform var(--transition-fast),
    box-shadow var(--transition-fast);
  box-shadow: var(--shadow-sm);
}

.signup-link:hover {
  background-color: var(--bg-color);
  color: var(--primary-dark);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

/* User menu */
.user-menu {
  position: relative;
}

.user-menu-button {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-md);
  transition: background-color var(--transition-fast);
}

.user-menu-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: white;
  color: var(--primary-color);
  font-weight: bold;
  font-size: 0.875rem;
}

.user-avatar.large {
  width: 48px;
  height: 48px;
  font-size: 1.2rem;
}

.username {
  font-weight: 500;
}

.arrow-icon {
  font-size: 0.6rem;
  transition: transform var(--transition-fast);
}

.arrow-icon.open {
  transform: rotate(180deg);
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  min-width: 200px;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  margin-top: var(--space-xs);
  z-index: 1;
}

.dropdown-item {
  display: block;
  padding: var(--space-md);
  color: var(--text-color);
  text-decoration: none;
  transition: background-color var(--transition-fast);
  text-align: left;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  font-family: inherit;
}

.dropdown-item:hover {
  background-color: var(--bg-light);
  color: var(--primary-color);
}

.dropdown-divider {
  height: 1px;
  background-color: var(--border-color);
  margin: 0;
}

.logout-btn {
  color: var(--error-color);
}

/* Mobile Menu */
.mobile-menu-btn {
  display: none;
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  position: relative;
  cursor: pointer;
  z-index: 1001;
  padding: var(--space-xs);
  border-radius: var(--radius-sm);
}

.menu-bar {
  display: block;
  width: 24px;
  height: 2px;
  background-color: white;
  position: absolute;
  left: 6px;
  transition: 
    transform var(--transition-normal),
    opacity var(--transition-normal);
}

.menu-bar:nth-child(1) {
  top: 10px;
}

.menu-bar:nth-child(2) {
  top: 17px;
}

.menu-bar:nth-child(3) {
  top: 24px;
}

.mobile-menu-btn.open .menu-bar:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.mobile-menu-btn.open .menu-bar:nth-child(2) {
  opacity: 0;
}

.mobile-menu-btn.open .menu-bar:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.mobile-nav {
  display: none;
  position: fixed;
  top: 70px;
  left: 0;
  width: 100%;
  height: calc(100vh - 70px);
  background-color: var(--bg-color);
  padding: var(--space-lg) var(--space-md);
  flex-direction: column;
  gap: var(--space-sm);
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  z-index: 999;
}

.mobile-nav .nav-link {
  color: var(--text-color);
  padding: var(--space-lg) var(--space-md);
  border-radius: var(--radius-md);
  gap: var(--space-sm);
  transition: background-color var(--transition-fast);
  font-size: 1.1rem;
  font-weight: 500;
}

.mobile-nav .nav-link:hover,
.mobile-nav .nav-link.active {
  background-color: var(--primary-light);
  color: var(--primary-color);
}

.mobile-nav .nav-link.active::after {
  display: none;
}

.nav-icon {
  font-size: 1.2rem;
  margin-right: var(--space-sm);
}

.mobile-nav-actions {
  margin-top: auto;
  padding-top: var(--space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
  border-top: 1px solid var(--border-color);
}

.user-info {
  display: flex;
  align-items: center;
  gap: var(--space-md);
  padding: var(--space-md);
  background-color: var(--bg-light);
  border-radius: var(--radius-md);
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-email {
  font-size: 0.875rem;
  color: var(--text-light);
}

.mobile-actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.auth-buttons {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  padding: var(--space-md) 0;
}

.btn-block {
  width: 100%;
}

/* Slide down animation for mobile menu */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform var(--transition-normal), opacity var(--transition-normal);
  transform-origin: top center;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}

@media (max-width: 992px) {
  .navbar-container {
    padding-left: var(--space-md);
    padding-right: var(--space-md);
  }
  
  .desktop-nav {
    gap: var(--space-lg);
  }
}

@media (max-width: 768px) {
  .navbar-container {
    height: 60px;
  }
  
  .mobile-nav {
    top: 60px;
    height: calc(100vh - 60px);
    display: flex;
    padding: var(--space-md);
  }
  
  .mobile-nav .nav-link {
    padding: var(--space-md);
    font-size: 1rem;
  }
  
  .desktop-nav {
    display: none;
  }
  
  .mobile-menu-btn {
    display: block;
  }
  
  .logo-text {
    font-size: 1.25rem;
  }
  
  .nav-icon {
    font-size: 1.1rem;
    min-width: 1.5rem;
  }
}

@media (max-width: 480px) {
  .navbar-container {
    padding-left: var(--space-sm);
    padding-right: var(--space-sm);
  }
  
  .beta-label {
    font-size: 0.35em;
    padding: 1px 4px;
    margin-left: 4px;
  }
}

/* Add beta label styles */
.beta-label {
  font-size: 0.4em;
  font-weight: 500;
  color: var(--text-inverse);
  background-color: rgba(255, 255, 255, 0.2);
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
  vertical-align: super;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Install button wrapper styles */
.install-button-wrapper {
  display: flex;
  justify-content: center;
  padding: var(--space-md) 0;
  margin: var(--space-sm) 0;
}

/* Install nav link styling - looks exactly like other nav items */
.install-nav-link {
  /* Inherits all nav-link styles automatically */
}

/* Desktop install button - clean navigation style */
.desktop-nav .install-button {
  background: transparent;
  border: none;
  color: white;
  font-size: 1rem;
  font-weight: 500;
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  transition: all var(--transition-normal);
  box-shadow: none;
  position: relative;
}

.desktop-nav .install-button:hover {
  color: rgba(255, 255, 255, 0.9);
  transform: none;
  box-shadow: none;
}


/* Install button wrapper styling for mobile */
.install-button-wrapper .install-button {
  background: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: white;
  font-size: 1rem;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.install-button-wrapper .install-button:hover {
  background: var(--primary-dark);
  border-color: var(--primary-dark);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}
</style>